<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NotificationsAPI from 'dashboard/api/notifications';

import PageHeader from 'dashboard/components-next/masifi/page-header/PageHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'next/avatar/Avatar.vue';

// Icono por tipo de notificación (fallback cuando no hay agente asignado).
const TYPE_ICONS = {
  conversation_creation: 'i-lucide-message-square-plus',
  conversation_assignment: 'i-lucide-user-plus',
  assigned_conversation_new_message: 'i-lucide-message-circle',
  participating_conversation_new_message: 'i-lucide-message-circle',
  conversation_mention: 'i-lucide-at-sign',
  sla_missed_first_response: 'i-lucide-alarm-clock',
  sla_missed_next_response: 'i-lucide-alarm-clock',
  sla_missed_resolution: 'i-lucide-alarm-clock',
};

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
// El badge en vivo (campana + header) vive en el store; lo mantenemos sincronizado
// desde el server tras cada acción de leído. La lista, en cambio, es local.
const unreadCount = useMapGetter('notifications/getUnreadCount');
const hasUnread = computed(() => (unreadCount.value || 0) > 0);

const filter = ref('all'); // 'all' | 'unread'
const items = ref([]);
const page = ref(1);
const hasMore = ref(false);
const isLoading = ref(false);
const isMarkingAll = ref(false);

// El finder por default devuelve solo NO leídas; incluir 'read' = todas (read + unread).
const statusParam = computed(() =>
  filter.value === 'all' ? 'read' : undefined
);

const isFirstLoad = computed(() => isLoading.value && items.value.length === 0);
const showEmptyState = computed(
  () => !isLoading.value && items.value.length === 0
);

let loadToken = 0;
const fetchPage = async ({ reset = false } = {}) => {
  loadToken += 1;
  const token = loadToken;
  if (reset) page.value = 1;
  isLoading.value = true;
  try {
    const {
      data: {
        data: { payload, meta },
      },
    } = await NotificationsAPI.get({
      page: page.value,
      status: statusParam.value,
    });
    if (token !== loadToken) return;
    items.value = reset ? payload : [...items.value, ...payload];
    // hasMore desde el total real (meta.count), no `payload.length >= 15`
    // (que dejaba "Cargar más" visible con exactamente N*15 notificaciones).
    hasMore.value = items.value.length < (meta?.count ?? 0);
  } catch (error) {
    if (token === loadToken) hasMore.value = false;
  } finally {
    if (token === loadToken) isLoading.value = false;
  }
};

const loadMore = () => {
  if (!hasMore.value || isLoading.value) return;
  page.value += 1;
  fetchPage();
};

const setFilter = value => {
  if (filter.value === value) return;
  filter.value = value;
};

watch(filter, () => fetchPage({ reset: true }));
onMounted(() => {
  fetchPage({ reset: true });
  store.dispatch('notifications/unReadCount');
});

const typeLabel = notification =>
  t(`NOTIFICATIONS_INDEX.TYPE_LABEL.${notification.notification_type}`);
const typeIcon = notification =>
  TYPE_ICONS[notification.notification_type] || 'i-lucide-bell';
const assigneeOf = notification =>
  notification.primary_actor?.meta?.assignee || null;
const conversationDisplayId = notification =>
  notification.primary_actor?.id || null;

// Marca leído vía API directa (no toca los records del store -> sin crash en
// READ_NOTIFICATION) y resincroniza el badge desde el server.
const markRead = async notification => {
  if (notification.read_at) return;
  notification.read_at = new Date().toISOString();
  try {
    await NotificationsAPI.read(
      notification.primary_actor_type,
      notification.primary_actor_id
    );
  } finally {
    store.dispatch('notifications/unReadCount');
  }
};

const onMarkAllRead = async () => {
  if (!hasUnread.value || isMarkingAll.value) return;
  useTrack(ACCOUNT_EVENTS.MARK_AS_READ_NOTIFICATIONS);
  isMarkingAll.value = true;
  const now = new Date().toISOString();
  items.value.forEach(item => {
    if (!item.read_at) item.read_at = now;
  });
  try {
    await NotificationsAPI.readAll();
  } finally {
    store.dispatch('notifications/unReadCount');
    isMarkingAll.value = false;
  }
};

const openNotification = notification => {
  useTrack(ACCOUNT_EVENTS.OPEN_CONVERSATION_VIA_NOTIFICATION, {
    notificationType: notification.notification_type,
  });
  markRead(notification);
  const conversationId = conversationDisplayId(notification);
  if (conversationId) {
    router.push(
      `/app/accounts/${accountId.value}/conversations/${conversationId}`
    );
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <PageHeader>
      <template #title>
        <div class="flex items-center gap-2 min-w-0">
          <h1 class="text-xl font-semibold truncate text-n-slate-12">
            {{ t('NOTIFICATIONS_INDEX.TITLE') }}
          </h1>
          <span
            v-if="hasUnread"
            class="px-1.5 py-px text-xs font-medium leading-none rounded-full text-n-ruby-11 bg-n-ruby-3"
          >
            {{ unreadCount }}
          </span>
        </div>
      </template>
      <template #actions>
        <Button
          v-if="hasUnread"
          faded
          slate
          sm
          icon="i-lucide-check-check"
          :label="t('NOTIFICATIONS_INDEX.MARK_ALL_READ')"
          :is-loading="isMarkingAll"
          @click="onMarkAllRead"
        />
      </template>
      <template #below>
        <div class="flex items-center gap-1">
          <Button
            :faded="filter !== 'all'"
            :slate="filter !== 'all'"
            :blue="filter === 'all'"
            sm
            :label="t('NOTIFICATIONS_INDEX.TABS.ALL')"
            @click="setFilter('all')"
          />
          <Button
            :faded="filter !== 'unread'"
            :slate="filter !== 'unread'"
            :blue="filter === 'unread'"
            sm
            :label="t('NOTIFICATIONS_INDEX.TABS.UNREAD')"
            @click="setFilter('unread')"
          />
        </div>
      </template>
    </PageHeader>

    <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">
      <!-- Primera carga -->
      <div v-if="isFirstLoad" class="flex justify-center py-16">
        <Spinner />
      </div>

      <!-- Vacío -->
      <div
        v-else-if="showEmptyState"
        class="flex flex-col items-center justify-center gap-3 py-20 text-center"
      >
        <span class="i-lucide-bell-off size-8 text-n-slate-9" />
        <p class="m-0 text-sm text-n-slate-10">
          {{
            filter === 'unread'
              ? t('NOTIFICATIONS_INDEX.EMPTY.UNREAD')
              : t('NOTIFICATIONS_INDEX.EMPTY.ALL')
          }}
        </p>
      </div>

      <!-- Lista -->
      <div v-else class="w-full max-w-3xl mx-auto">
        <ul class="flex flex-col m-0 list-none">
          <li
            v-for="notification in items"
            :key="notification.id"
            class="flex items-start gap-3 px-4 py-3 transition-colors border-b cursor-pointer group sm:px-6 border-n-weak hover:bg-n-alpha-1"
            :class="{ 'bg-n-alpha-1': !notification.read_at }"
            @click="openNotification(notification)"
          >
            <div class="relative flex-shrink-0 mt-0.5">
              <Avatar
                v-if="assigneeOf(notification)"
                :src="assigneeOf(notification).thumbnail"
                :name="assigneeOf(notification).name"
                :size="32"
                rounded-full
              />
              <div
                v-else
                class="grid rounded-full size-8 bg-n-alpha-2 place-content-center"
              >
                <span
                  :class="typeIcon(notification)"
                  class="size-4 text-n-slate-11"
                />
              </div>
              <span
                v-if="!notification.read_at"
                class="absolute -top-0.5 -right-0.5 rounded-full size-2.5 bg-n-ruby-9 ring-2 ring-n-background"
              />
            </div>

            <div class="flex flex-col min-w-0 gap-0.5 grow">
              <div class="flex items-center gap-2">
                <span
                  class="text-sm truncate text-n-slate-12"
                  :class="{ 'font-semibold': !notification.read_at }"
                >
                  {{ notification.push_message_title }}
                </span>
                <span class="flex-shrink-0 ml-auto text-xs text-n-slate-10">
                  {{ dynamicTime(notification.last_activity_at) }}
                </span>
              </div>
              <span
                v-if="notification.push_message_body"
                class="text-sm text-n-slate-11 line-clamp-1"
              >
                {{ notification.push_message_body }}
              </span>
              <div class="flex items-center gap-2 mt-0.5">
                <span
                  class="px-1.5 py-px text-xs rounded-md text-n-slate-11 bg-n-alpha-2"
                >
                  {{ typeLabel(notification) }}
                </span>
                <span
                  v-if="conversationDisplayId(notification)"
                  class="text-xs text-n-slate-10"
                >
                  {{ `#${conversationDisplayId(notification)}` }}
                </span>
              </div>
            </div>

            <Button
              v-if="!notification.read_at"
              v-tooltip="t('NOTIFICATIONS_INDEX.MARK_READ')"
              ghost
              slate
              xs
              icon="i-lucide-check"
              class="flex-shrink-0 transition-opacity opacity-0 group-hover:opacity-100"
              @click.stop="markRead(notification)"
            />
          </li>
        </ul>

        <div v-if="hasMore" class="flex justify-center px-4 py-4">
          <Button
            faded
            slate
            sm
            :is-loading="isLoading"
            :label="t('NOTIFICATIONS_INDEX.LOAD_MORE')"
            @click="loadMore"
          />
        </div>
      </div>
    </div>
  </div>
</template>
